import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  FormControlLabel,
  Snackbar,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import CustomNoRowsOverlay from "./gridNoRowOverLay";
import { DeleteConfirmDialog } from "./EquipmentsTable";
import { hasBusinessUnitData } from "../utils/businessUnit";
import { invoiceUrls } from "../utils/invoiceLinks";
import {
  dismissPotentialAgreements,
  getPotentialAgreements,
} from "../services/potentialAgreementServices";

// The Agreements screen's "Potential" filter (#418): invoice lines that look like a JB plan sale
// -- "JB Warranty", a code with one character off -- but never became an agreement, because the
// batch only matches a SKU that is exactly an active plan code.
//
// These rows are not agreements (there is no tblAgreement row yet), so this is its own fetch and
// its own columns rather than one more client-side predicate over AgreementsGrid's list. Unlike
// that grid it pages and filters on the server (tenant, business units, quick search, tier): across
// every dealer the list is far too long to fetch whole.
//
// A row is edited exactly like a Pending agreement: Edit opens the same AgreementEquimentDialog,
// with the SKU as typed in its plan search box and the same equipment rows. Saving there creates
// the agreement(s) -- one per unit of quantity -- and they then show under Pending. "Not an
// agreement" dismisses a line for every user of the dealer. The Dismissed switch lists those lines,
// and editing one and saving accepts it: that is how a dismiss is undone.

// tblPotentialAgreement.status, as the list route's status parameter takes it.
const STATUS = Object.freeze({ NEW: "New", DISMISSED: "Dismissed" });

const messageOf = (err, fallback) => err?.response?.data?.message || fallback;

// Module-level, with its data passed through slotProps: a toolbar component declared inside the
// grid is a new component on every render, so React remounts it -- and an open "Filter by" menu
// closes the moment anything re-renders the grid (the success notice after an accept does).
const PotentialToolbar = ({
  filterControl,
  selectedCount,
  busy,
  onDismissSelected,
  showOther,
  otherCount,
  onToggleOther,
  status,
  onStatusChange,
}) => (
  <GridToolbarContainer
    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", p: 1 }}
  >
    <GridToolbarQuickFilter />
    {filterControl}
    <ToggleButtonGroup
      size="small"
      exclusive
      value={status}
      onChange={(e, next) => next && onStatusChange(next)}
    >
      <ToggleButton value={STATUS.NEW}>New</ToggleButton>
      <ToggleButton value={STATUS.DISMISSED}>Dismissed</ToggleButton>
    </ToggleButtonGroup>
    {/* The non-JB tier: broad keyword matches (memberships, Labor, Install), off by default. */}
    <FormControlLabel
      control={<Switch checked={showOther} onChange={(e) => onToggleOther(e.target.checked)} />}
      label={`Show other matches (install, warranty, plan, labor): ${otherCount.toLocaleString()}`}
    />
    {status === STATUS.NEW && (
      <Button variant="outlined" color="warning" disabled={!selectedCount || busy} onClick={onDismissSelected}>
        Not agreements ({selectedCount})
      </Button>
    )}
  </GridToolbarContainer>
);

// The row reshaped into what AgreementEquimentDialog and AgreementInfo read off an agreement.
// There is no agreement id yet; potentialAgreementId is what the dialog saves through instead.
const asAgreement = (row) => ({
  ...row,
  id: undefined,
  potentialAgreementId: row.id,
  productCode: row.itemSKUName,
  poNumber: row.invoiceNumber,
  location: [row.addressLine1, row.city, row.state].filter(Boolean).join(", "),
  missingEquipment: 1,
  isReadyForJBW: 0,
});

const PotentialAgreementsGrid = ({
  dealer,
  showDealer = false,
  tenant,
  businessUnits,
  filterControl,
  openEquimentDialog,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dismissIds, setDismissIds] = useState([]);
  const [notice, setNotice] = useState(null);
  const [includeOther, setIncludeOther] = useState(false);
  const [otherCount, setOtherCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState(STATUS.NEW);
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // Admin screens list every dealer (id 0); a dealer's own screen lists only theirs.
  const dealerId = showDealer ? 0 : dealer?.dealerId;
  // As the request sends it, and a stable dependency however the parent builds the array.
  const businessUnitIds = businessUnits?.length ? businessUnits.join(",") : "";

  const load = useCallback(() => {
    if (dealerId === undefined || dealerId === null) return;
    setLoading(true);
    setLoadError(null);
    getPotentialAgreements(dealerId, {
      status,
      include: includeOther ? "other" : undefined,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      tenantId: tenant || undefined,
      businessUnitIds: businessUnitIds || undefined,
      q: search || undefined,
    })
      .then((res) => {
        setRows(res.data.rows);
        setTotal(res.data.total);
        setOtherCount(res.data.otherCount);
      })
      .catch((err) => {
        console.error("Unable to load potential agreements", err);
        setLoadError(messageOf(err, "Potential agreements could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [dealerId, status, includeOther, paginationModel, tenant, businessUnitIds, search]);

  useEffect(load, [load]);

  // Any filter change goes back to the first page.
  useEffect(() => {
    setPaginationModel((m) => (m.page === 0 ? m : { ...m, page: 0 }));
    setSelected([]);
  }, [status, includeOther, tenant, businessUnitIds, search]);

  const showBusinessUnit = useMemo(() => hasBusinessUnitData(rows), [rows]);

  const dismiss = (ids) => {
    setBusy(true);
    dismissPotentialAgreements(ids)
      .then(() => {
        setSelected((prev) => prev.filter((id) => !ids.includes(id)));
        load(); // so the page and its total stay right
        setNotice(`${ids.length} line${ids.length === 1 ? "" : "s"} marked as not an agreement.`);
      })
      .catch((err) => setNotice(messageOf(err, "The lines could not be dismissed.")))
      .finally(() => setBusy(false));
  };

  const handleConfirmDismiss = (confirmed) => {
    const ids = dismissIds;
    setDismissIds([]);
    if (confirmed && ids.length) dismiss(ids);
  };

  const openInvoice = (row) => window.open(invoiceUrls(row, dealer?.crmSystem).url, "_blank");

  const columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Action",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon fontSize="medium" />}
          label="Edit"
          className="textPrimary"
          onClick={() => openEquimentDialog(asAgreement(params.row), "create")}
          color="secondary"
          title="Edit Equipment"
        />,
        // Only a New line can be dismissed; a Dismissed one is undone by Edit and Save.
        ...(status === STATUS.NEW
          ? [
              <GridActionsCellItem
                key="dismiss"
                icon={<DoNotDisturbIcon fontSize="medium" />}
                label="Not an agreement"
                onClick={() => setDismissIds([params.row.id])}
                showInMenu
              />,
            ]
          : []),
      ],
    },
    { field: "dealerName", headerName: "Dealer", width: 180 },
    {
      field: "invoiceNumber",
      headerName: "Invoice",
      width: 130,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ color: "primary.main", cursor: "pointer", textDecoration: "underline" }}
          onClick={() => openInvoice(params.row)}
        >
          {params.value}
        </Typography>
      ),
    },
    { field: "invoiceDate", headerName: "Invoice Date", width: 110 },
    { field: "customerName", headerName: "Customer", width: 170 },
    { field: "businessUnitName", headerName: "Business Unit", width: 150 },
    { field: "itemSKUName", headerName: "SKU on Invoice", width: 190 },
    { field: "itemDisplayName", headerName: "Item Name", width: 190 },
    { field: "quantity", headerName: "Qty", type: "number", width: 60 },
    {
      field: "totalCost",
      headerName: "Line Total",
      type: "number",
      width: 100,
      valueGetter: (params) => Number(params.value),
      valueFormatter: (params) =>
        params.value == null ? "" : `$${Number(params.value).toLocaleString()}`,
    },
    { field: "flagReason", headerName: "Flagged Because", width: 230 },
  ];

  // No skeleton in place of the grid while loading: the grid's own overlay keeps the toolbar, and
  // the quick filter's focus, mounted across pages and searches.
  return (
    <>
      {loadError && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Alert severity="error" sx={{ flexGrow: 1 }}>
            {loadError}
          </Alert>
          <Button onClick={load}>Retry</Button>
        </Stack>
      )}
      <DataGrid
        rows={rows}
        rowCount={total}
        loading={loading}
        getRowId={(row) => row.id}
        columns={columns}
        checkboxSelection={status === STATUS.NEW}
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
        rowSelectionModel={selected}
        onRowSelectionModelChange={setSelected}
        getRowHeight={() => "auto"}
        // Render every row on the page (at most 100): with auto row heights the grid otherwise
        // leaves rows past the fold unrendered, and a dealer's whole list is small enough not to
        // need windowing. Keeps what is on the page findable -- by Ctrl-F and by the e2e spec.
        disableVirtualization
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[25, 50, 100]}
        filterMode="server"
        onFilterModelChange={(model) => setSearch((model.quickFilterValues || []).join(" "))}
        columnVisibilityModel={{ dealerName: showDealer, businessUnitName: showBusinessUnit }}
        disableColumnFilter
        disableColumnMenu
        slots={{ noRowsOverlay: CustomNoRowsOverlay, toolbar: PotentialToolbar }}
        slotProps={{
          toolbar: {
            filterControl,
            selectedCount: selected.length,
            busy,
            onDismissSelected: () => setDismissIds(selected),
            showOther: includeOther,
            otherCount,
            onToggleOther: setIncludeOther,
            status,
            onStatusChange: setStatus,
          },
        }}
        sx={{
          "& .MuiDataGrid-columnHeaderTitleContainer": { whiteSpace: "normal", lineHeight: "normal" },
          "& .MuiDataGrid-columnHeader": { height: "unset !important" },
        }}
      />
      <DeleteConfirmDialog
        open={dismissIds.length > 0}
        onClose={handleConfirmDismiss}
        title={dismissIds.length === 1 ? "Not an agreement" : "Not agreements"}
        message={`Mark ${dismissIds.length} line${dismissIds.length === 1 ? "" : "s"} as not an agreement? They will be removed from this list for everyone at the dealer.`}
      />
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="info" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PotentialAgreementsGrid;
